/**
 * ============================================
 * LOGIN PAGE - Página de Autenticación
 * ============================================
 *
 * Página de inicio de sesión para el LIMS.
 * Diseño centrado con el logo de Molina Valdez.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts";
import { useApiError } from "@/hooks/use-api-error";
import { Eye, EyeOff, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageSelector } from "@/components/language-selector";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Hook de autenticación - nos da acceso a la función login()
  const { login } = useAuth();

  // Hook de traducción de errores API
  const { getErrorMessage } = useApiError();

  // Estado del formulario
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Guardamos el error original para poder re-traducirlo si cambia el idioma
  const [error, setError] = useState<{ type: "validation" | "api"; data: unknown } | null>(null);

  // Mensaje de error traducido (se recalcula cuando cambia el idioma)
  const errorMessage = error
    ? error.type === "validation"
      ? t(error.data as string)
      : getErrorMessage(error.data)
    : "";

  /**
   * Manejar submit del formulario
   *
   * Flujo:
   * 1. Validar que los campos no estén vacíos
   * 2. Llamar a login() del AuthContext (hace POST a /api/auth/login/)
   * 3. Si éxito: redirigir al dashboard
   * 4. Si error: mostrar mensaje traducido al usuario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validación básica en frontend
    if (!email || !password) {
      setError({ type: "validation", data: "auth.errorEmptyFields" });
      return;
    }

    setIsLoading(true);

    try {
      // Llamar al backend via AuthContext
      // login() hace POST a /api/auth/login/ y guarda tokens en localStorage
      await login(email, password);

      // Login exitoso - redirigir al dashboard
      navigate("/", { replace: true });
    } catch (err) {
      // Guardar el error original para poder re-traducirlo si cambia el idioma
      setError({ type: "api", data: err });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header con controles de tema e idioma */}
      <header className="flex justify-end gap-2 p-4">
        <LanguageSelector />
        <ThemeToggle />
      </header>

      {/* Contenido principal centrado */}
      <main className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            {/* Logo */}
            <div className="flex justify-center">
              <img
                src="/logo-transparent.png"
                alt="Molina Valdez"
                className="h-24 w-24 object-contain"
              />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold">Laboratorio Molina Valdez</CardTitle>
              <CardDescription className="text-base">{t("auth.loginSubtitle")}</CardDescription>
            </div>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {/* Mensaje de error */}
              {errorMessage && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {errorMessage}
                </div>
              )}

              {/* Campo de email */}
              <div className="space-y-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {/* Campo de contraseña */}
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="sr-only">
                      {showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4 pt-6">
              {/* Botón de login */}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("auth.loggingIn")}
                  </>
                ) : (
                  t("auth.login")
                )}
              </Button>

              {/* Link de olvidé contraseña */}
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground"
                onClick={() => {
                  // TODO: Implementar recuperación de contraseña
                  alert(t("auth.forgotPasswordMessage"));
                }}
              >
                {t("auth.forgotPassword")}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Laboratorio Molina Valdez. Honduras.
      </footer>
    </div>
  );
}
